/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/

import { DateHelper } from '@src/app/shared/dateHelper';
import { FieldSet, LAYOUT_OPTIONS } from '@src/app/shared/form/field-set/field-set.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { AbstractObject, uuid } from '@src/app/model/abstract-object';
import { Action } from '@src/app/model/action';
import { Area } from '@src/app/model/area';
import { Attachment } from '@src/app/model/attachment';
import { BillingItem } from '@src/app/model/billing';
import { Core } from '@src/app/model/core';
import { Cycle } from '@src/app/model/cycle';

import { Frequency } from '@src/app/model/frequency';
import { MailMerge } from '@src/app/model/mailMerge';
import { Memo } from '@src/app/model/memo';
import { Payz } from '@src/app/model/Payz';
import { PayzMandate } from '@src/app/model/PayzMandate';
import { Person } from '@src/app/model/person';
import { PersonUnitRole } from '@src/app/model/person-unit-role';
import { SchedulePortion } from '@src/app/model/schedule-portion';
import { ServiceRequest } from '@src/app/model/serviceRequest';
import { Txn } from '@src/app/model/txn';
import { UnitCategory } from '@src/app/model/unit-category';
import { UnitType } from '@src/app/model/unit-type';

export class Unit extends AbstractObject {
    title: string;
    ownerId: uuid;
    owner: Person;
    ownerDetails: string;
    ownerOccupied: boolean;
    ownerFullName: string;

    numberCarSpaces: number;
    carSpaces: string;

    typeId: uuid;
    type: UnitType;
    areaId: uuid;
    area: Area;
    coreId: uuid;
    core: Core;
    floor: number;
    bedrooms: number;
    categoryId: uuid;
    category: UnitCategory;
    fireSafetyCheck: string; // Date...
    arrearsStageId: uuid = 0;
    frequencyId: uuid = 1;
    frequency: Frequency;
    size = 1;
    address: string;
    people: PersonUnitRole[] = [];
    pastPeople: PersonUnitRole[] = [];
    transactions: Txn[] = [];
    portions: SchedulePortion[] = [];
    /** Calculated fields */
    txnWithPayments: Txn[] = [];
    actions: Action[] = [];
    mailMerges?: MailMerge [] = [];
    memos: Memo[] = [];
    billingItems: BillingItem[] = [] //myUnit Only - If proposed budget available
    proposedBillingTotal: number; //calculated
    myProposedBillingTotal: number; // calculated

    fullPayDiscount: number;
    fullPayAmount: number;

    invoices: number;

    mandateDiscount: number;
    mandateFullPayAmount: number;

    overdue = 0;
    yearStart = 0;
    paymentsPerAnnum = 0;
    remainingPayments = 0;
    currentCharges = 0;
    currentPays = 0;
    oldPay = 0;
    newPay = 0;
    paysLeft = 0;
    outstanding = 0;
    balance = 0;
    arrears = 0;
        
    inProgressPayz: Payz[] = [];
    payzMandate: PayzMandate;

    requests: ServiceRequest[] = [];
    attachments?: Attachment[] = [];
    currentTxn?: Txn[];

    billingAddress?: string;

    constructor(o: Partial<Unit> = {}) {
        super(o);
    }

    static getFullName(o: Unit): string {
        let retValue = o && o.name ? o.name : '';
        if (o?.address) {
            retValue += ' - ' + o.address.split('\n')[0];
        } else {
            if (o?.type) {
                retValue += ' - ' + o.type.name;
            }
        }
        return retValue;
    }
    static ArrearsStages = [
        {id: 0, name: '', description: $localize `Not in the arrears process`},
        {id: 10, name: $localize `Agreed Plan`, description: $localize `Owner is on agreed plan`},
        {id: 20, name: $localize `First Warning`, description: $localize `First formal warning has been issued`},
        {id: 30, name: $localize `Second Warning`, description: $localize `Second formal warning has been issued`},
        {id: 40, name: $localize `Final Warning`, description: $localize `Final formal warning has been issued`},
        {id: 50, name: $localize `Debt Collection`, description: $localize `Formal Debt Collection Started`},
        {id: 60, name: $localize `With Solicitor`, description: $localize `File with solicitor for processing`},
        {id: 70, name: $localize `Legal Started`, description: $localize `Court proceedings have been initiated`},
        {id: 80, name: $localize `Judgement Obtained`, description: $localize `Court judgement has been obtained`},
        {id: 90, name: $localize `Enforcement Requested`, description: $localize `Formal enforcement applied for`},
        {id: 100, name: $localize `Enforcement Obtained`, description: $localize `Garnish order/Sheriff/Attachment of earnings obtained`},
        {id: 110, name: $localize `Solicitors Undertaking Received`, description: $localize `Legal commitment received from a solicitor to discharge the debt`},
    ]


    static calculatePaymentSchedule(o: Unit, currentCycle: Cycle) {
        let outstanding = 0;
        let balance = 0;
        let currentPays = 0;
        let arrears = 0;
        console.error('This version not used, a little unreliable in calculations if there are previous owner transactions not included, especially if there are outstanding amounts from previous owner');

        for (const t of o.transactions) {
            outstanding += t.outstanding;
            balance += (t.debit - t.credit);
            if (t.txnDate < currentCycle.from) {
                arrears += t.outstanding; // Sort transactions resets this
            }
            if (t.txnCycleId === currentCycle.id) {
                if (t.typeId === Txn.TYPE.BANK_IN_ALLOC.id || t.typeId === Txn.TYPE.BANK_IN_ON_ACCT.id) {
                    currentPays += t.credit;
                }
            }
        }

        const daysIn = DateHelper.isoDaysSince(currentCycle.from);
        const daysTo = DateHelper.isoDaysTo(currentCycle.to);

        let paysExpected = o.frequency.perAnnum;
        if (daysTo > 0) {
            paysExpected = 1 + Math.trunc(daysIn / (daysIn + daysTo) * o.frequency.perAnnum);
        }

        const paysLeft = o.frequency.perAnnum - paysExpected;

        o.outstanding = outstanding; // Outstanding it total outstanding, usually the same as the balance
        o.balance = balance; // If there is an outstanding amount, offset by an outstanding amount from previous owner, balance could be different
        o.arrears = arrears;

        const currentDate = new Date().toISOString().slice(0, 10);

        if (currentDate <= currentCycle.fullPayBy) {
            o.fullPayDiscount = Math.round(((outstanding - arrears) * currentCycle.fullPayDiscount * 100) / 100);
        } else {
            o.fullPayDiscount = 0;
        }
        o.fullPayAmount = outstanding - o.fullPayDiscount;

        o.mandateDiscount = Math.round(((outstanding - arrears) * currentCycle.mandateDiscount * 100) / 100);
        o.mandateFullPayAmount = outstanding - o.mandateDiscount;


        o.yearStart = outstanding + currentPays;
        o.oldPay = o.yearStart / o.frequency.perAnnum;
        o.currentPays = currentPays;
        o.overdue = Math.max(0, (paysExpected * o.oldPay) - currentPays);
        o.newPay = paysLeft ? o.outstanding / paysLeft : 0;
        o.paysLeft = paysLeft;

        return o;
    }

    static pickDialogFormConfig(): FormConfig {
        return new FormConfig({
            fieldSet: new FieldSet({
                fields: [
                    FormTextComponent.make('Name', 'name'),
                    FormTextComponent.make('Address', 'address'),
                ],
                formLayout: LAYOUT_OPTIONS.singleCol,
            }),
            title: $localize`Units`,
            mode: 'list',
        });
    }

    private static getKey(t: Txn) {
        return t.txnDate + t.id;
    }

    static sortUnitTransactions(transactions: Txn[]) {
        const payMap = new Map<uuid, Txn>();
        const txnWithPayments = [];
        let balance = 0;
        for (const txn of transactions) {
            balance -= txn.credit;
            balance += txn.debit;
            if (txn.typeId === Txn.TYPE.BANK_IN_ALLOC.id || txn.typeId === Txn.TYPE.BANK_IN_ON_ACCT.id) {
                if (!payMap.has(txn.parentId)) {
                    const p = new Txn();
                    p.teamId = txn.teamId;
                    p.id = txn.parentId;
                    p.typeId = Txn.TYPE.BANK_IN.id;
                    p.type = Txn.TYPE.BANK_IN;
                    p.txnDate = txn.txnDate;
                    p.credit = txn.credit;
                    p.outstanding = txn.outstanding;
                    p.refNr = txn.parentRefNr;
                    p.ownerChangeId = txn.ownerChangeId;
                    p.notes = `${txn.parent?.notes ?? ''}${txn.notes ? ' - ' + txn.notes : ''}`;
                    payMap.set(txn.parentId, p);
                    txnWithPayments.push(p);
                } else {
                    payMap.get(txn.parentId).credit += txn.credit;
                    payMap.get(txn.parentId).outstanding += txn.outstanding;
                    payMap.get(txn.parentId).notes += txn.notes ? ' - ' + txn.notes : '';
                }
            } else {
                txnWithPayments.push(txn);
            }
        }
        const txnWithPaymentsSorted = txnWithPayments.sort((a, b) => {
            const keyA = this.getKey(a);
            const keyB = this.getKey(b);
            if (keyA > keyB) {
                return -1;
            } else if (keyA < keyB) {
                return 1;
            } else {
                return 0;
            }
        });
        txnWithPaymentsSorted.forEach(txn => {
            txn.balance = balance;
            balance += txn.credit;
            balance -= txn.debit;
        })
        return txnWithPaymentsSorted;

    }
}
